import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calculator, Clock, Download, Calendar, Info, TrendingUp, Euro } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { useCasesAPI, analyticsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useCasesDetailed as mockUseCases } from '../data/mockData';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const ROICalculatorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [variables, setVariables] = useState({});
  const [useCase, setUseCase] = useState(null);
  const [loading, setLoading] = useState(true);
  // Flip-card state: true = show Hours Saved side, false = show Annual Savings (default)
  const [resultsFlipped, setResultsFlipped] = useState(false);
  
  // Analytics tracking
  const lastTrackedRef = useRef(null);
  const trackingTimeoutRef = useRef(null);

  // Fetch use case from API and merge with mockData for ROI config
  useEffect(() => {
    const fetchUseCase = async () => {
      try {
        const data = await useCasesAPI.getById(id);
        
        if (!data || !data.title) {
          console.log('No data from API, using mockData fallback');
          // Try to find by integer ID first (for direct mockData links)
          const mockById = mockUseCases.find(m => m.id?.toString() === id?.toString());
          if (mockById) {
            setUseCase(mockById);
            return;
          }
          throw new Error('Use case not found');
        }
        
        const normalizedTitle = data.title.toLowerCase().trim();
        console.log('Looking for ROI config for:', normalizedTitle);
        
        // Find matching mockData entry for ROI configuration by title
        // Uses flexible matching: exact match first, then partial/fuzzy match
        const mockMatch = mockUseCases.find(m => {
          const mockTitle = m.title?.toLowerCase().trim() || '';
          // Exact match
          if (mockTitle === normalizedTitle) return true;
          // Partial match: check if one contains the other (for cases like "Onboarding Chatbot" vs "Pre & Onboarding Chatbot")
          if (mockTitle.includes(normalizedTitle) || normalizedTitle.includes(mockTitle)) return true;
          // Domain-based fallback match: compare key words
          const normalizedWords = normalizedTitle.split(/[\s&]+/).filter(w => w.length > 3);
          const mockWords = mockTitle.split(/[\s&]+/).filter(w => w.length > 3);
          const matchingWords = normalizedWords.filter(w => mockWords.some(mw => mw.includes(w) || w.includes(mw)));
          return matchingWords.length >= 2; // At least 2 significant words match
        });
        
        console.log('Mock match found:', mockMatch?.title, 'has roiVariables:', !!mockMatch?.roiVariables);
        
        // Merge API data with mock ROI config
        const mergedUseCase = {
          ...data,
          roiVariables: mockMatch?.roiVariables || data?.roiVariables,
          roiCalculation: mockMatch?.roiCalculation || data?.roiCalculation,
          roiBreakdown: mockMatch?.roiBreakdown || data?.roiBreakdown,
        };
        
        setUseCase(mergedUseCase);
      } catch (error) {
        console.error('Failed to fetch use case:', error);
        // Fallback to mockData if API fails
        const mockMatch = mockUseCases.find(m => m.id?.toString() === id?.toString());
        if (mockMatch) {
          setUseCase(mockMatch);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchUseCase();
  }, [id]);

  // Initialize variables with defaults from use case
  useEffect(() => {
    if (useCase?.roiVariables) {
      const defaults = {};
      useCase.roiVariables.forEach((v) => {
        defaults[v.key] = v.default;
      });
      setVariables(defaults);
    }
  }, [useCase]);

  // Calculate ROI using the use case's calculation function
  const results = useMemo(() => {
    if (!useCase?.roiCalculation || Object.keys(variables).length === 0) {
      return { savedHours: 0, savings: 0 };
    }
    try {
      return useCase.roiCalculation(variables);
    } catch (e) {
      return { savedHours: 0, savings: 0 };
    }
  }, [useCase, variables]);

  // Get breakdown data
  const breakdown = useMemo(() => {
    if (!useCase?.roiBreakdown || Object.keys(variables).length === 0) {
      return [];
    }
    try {
      return useCase.roiBreakdown(variables);
    } catch (e) {
      return [];
    }
  }, [useCase, variables]);

  // Track ROI calculations (debounced to avoid excessive API calls)
  useEffect(() => {
    if (!useCase || !results || Object.keys(variables).length === 0) return;
    if (results.savings === 0 && results.savedHours === 0) return;
    
    // Debounce tracking - wait 2 seconds after last change before tracking
    if (trackingTimeoutRef.current) {
      clearTimeout(trackingTimeoutRef.current);
    }
    
    trackingTimeoutRef.current = setTimeout(() => {
      // Only track if values actually changed
      const currentKey = JSON.stringify({ variables, results });
      if (lastTrackedRef.current === currentKey) return;
      lastTrackedRef.current = currentKey;
      
      // Get session ID
      let sessionId = sessionStorage.getItem('analytics_session_id');
      if (!sessionId) {
        sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('analytics_session_id', sessionId);
      }
      
      analyticsAPI.trackROICalculation({
        use_case_id: id,
        use_case_title: useCase.title,
        user_id: user?.id || null,
        user_email: user?.email || null,
        variables: variables,
        results: results,
        session_id: sessionId
      });
    }, 2000);
    
    return () => {
      if (trackingTimeoutRef.current) {
        clearTimeout(trackingTimeoutRef.current);
      }
    };
  }, [useCase, id, variables, results, user]);

  const handleVariableChange = (key, value) => {
    setVariables((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleInputChange = (key, value, variable) => {
    let numValue = parseFloat(value) || 0;
    // Clamp to min/max
    numValue = Math.max(variable.min, Math.min(variable.max, numValue));
    handleVariableChange(key, numValue);
  };

  const resetToDefaults = () => {
    if (useCase?.roiVariables) {
      const defaults = {};
      useCase.roiVariables.forEach((v) => {
        defaults[v.key] = v.default;
      });
      setVariables(defaults);
    }
  };

  const getCostRange = () => {
    return useCase?.costRange || '€10,000 - 30,000';
  };

  const getPaybackPeriod = () => {
    const avgCost = useCase?.difficulty === 'low' ? 10000 : useCase?.difficulty === 'medium' ? 27500 : 55000;
    if (results.savings <= 0) return 'N/A';
    const months = Math.ceil((avgCost / results.savings) * 12);
    if (language === 'nl') {
      if (months > 36) return '> 3 jaar';
      if (months <= 1) return '< 1 maand';
      return `${months} maanden`;
    }
    if (months > 36) return '> 3 years';
    if (months <= 1) return '< 1 month';
    return `${months} months`;
  };

  const getROIPercentage = () => {
    const avgCost = useCase?.difficulty === 'low' ? 10000 : useCase?.difficulty === 'medium' ? 27500 : 55000;
    if (avgCost <= 0) return 0;
    return Math.round(((results.savings - avgCost) / avgCost) * 100);
  };

  const downloadReport = () => {
    // Generate a simple text report
    let report = `ROI RAPPORT: ${useCase?.title}\n`;
    report += `${'='.repeat(50)}\n\n`;
    report += `SAMENVATTING\n`;
    report += `-`.repeat(30) + '\n';
    report += `Geschatte jaarlijkse besparing: €${results.savings.toLocaleString()}\n`;
    report += `Uren bespaard per jaar: ${results.savedHours.toLocaleString()}\n`;
    report += `Implementatiekosten: ${getCostRange()}\n`;
    report += `Terugverdientijd: ${getPaybackPeriod()}\n`;
    report += `ROI: ${getROIPercentage()}%\n\n`;
    
    report += `GEBRUIKTE VARIABELEN\n`;
    report += `-`.repeat(30) + '\n';
    useCase?.roiVariables?.forEach((v) => {
      const value = variables[v.key];
      report += `${v.label}: ${v.unit === '€' ? '€' : ''}${value}${v.unit && v.unit !== '€' ? ` ${v.unit}` : ''}\n`;
    });
    
    report += `\nBEREKENING BREAKDOWN\n`;
    report += `-`.repeat(30) + '\n';
    breakdown.forEach((item) => {
      report += `${item.label}: ${item.value}\n`;
    });
    
    report += `\n\nFormule: ${useCase?.roiFormula}\n`;
    report += `\nRapport gegenereerd op: ${new Date().toLocaleDateString('nl-BE')}\n`;

    const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ROI-report-${useCase?.title?.replace(/\s+/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">{language === 'nl' ? 'Laden...' : 'Loading...'}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // ROI Calculator is now public - no access check needed

  if (!useCase) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-2xl font-bold text-gray-900">
              {language === 'nl' ? 'Use case niet gevonden' : 'Use case not found'}
            </h1>
            <Button onClick={() => navigate('/use-cases')} className="bg-blue-500 mt-4">
              {language === 'nl' ? 'Terug naar Use Cases' : 'Back to Use Cases'}
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-white">
        <Header />
        
        <main className="py-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Link */}
            <button
              onClick={() => navigate(`/use-cases/${id}`)}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Terug naar {useCase.title}
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-400 to-blue-500 rounded-2xl mb-4 shadow-lg">
                <Calculator className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                ROI Calculator
              </h1>
              <p className="text-lg text-gray-600">
                {useCase.title}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Variables */}
              <div className="lg:col-span-2 space-y-6">
                {/* Variables Card */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <span className="text-2xl">⚙️</span>
                      Pas variabelen aan
                    </h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetToDefaults}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      Reset naar standaard
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {useCase.roiVariables?.map((variable) => (
                      <div key={variable.key} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                            {variable.label}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button className="p-1.5 -m-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                                  <Info className="h-3.5 w-3.5 text-gray-400 hover:text-blue-500 transition-colors" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs p-3 bg-white shadow-lg border border-gray-200">
                                <p className="text-sm text-gray-700 leading-relaxed">
                                  {variable.tooltip || `Bereik: ${variable.min} - ${variable.max}`}
                                </p>
                                {variable.tooltip && (
                                  <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">
                                    Bereik: {variable.unit === '€' ? '€' : ''}{variable.min.toLocaleString()}{variable.unit && variable.unit !== '€' ? ` ${variable.unit}` : ''} - {variable.unit === '€' ? '€' : ''}{variable.max.toLocaleString()}{variable.unit && variable.unit !== '€' ? ` ${variable.unit}` : ''}
                                  </p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </label>
                          <div className="flex items-center gap-2">
                            {variable.unit === '€' && <span className="text-gray-500">€</span>}
                            <Input
                              type="number"
                              value={variables[variable.key] !== undefined ? variables[variable.key] : variable.default}
                              onChange={(e) => handleInputChange(variable.key, e.target.value, variable)}
                              className="w-28 text-right font-semibold"
                              min={variable.min}
                              max={variable.max}
                              step={variable.step}
                            />
                            {variable.unit && variable.unit !== '€' && (
                              <span className="text-gray-500 text-sm w-12">{variable.unit}</span>
                            )}
                          </div>
                        </div>
                        <Slider
                          value={[variables[variable.key] !== undefined ? variables[variable.key] : variable.default]}
                          onValueChange={([value]) => handleVariableChange(variable.key, value)}
                          min={variable.min}
                          max={variable.max}
                          step={variable.step}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-gray-400">
                          <span>{variable.unit === '€' ? `€${variable.min.toLocaleString()}` : variable.min}</span>
                          <span>{variable.unit === '€' ? `€${variable.max.toLocaleString()}` : variable.max}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Breakdown Card */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-2xl">📊</span>
                    Berekening breakdown
                  </h2>
                  
                  <div className="space-y-3">
                    {breakdown.map((item, index) => (
                      <div 
                        key={item.label || `row-${index}`} 
                        className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                      >
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-semibold text-gray-900">
                          {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Formula Display */}
                  <div className="mt-6 bg-blue-50 rounded-xl p-4">
                    <p className="text-sm font-medium text-blue-800 mb-1">Formule</p>
                    <code className="text-xs text-blue-600 font-mono break-all">
                      {useCase.roiFormula}
                    </code>
                  </div>
                </div>
              </div>

              {/* Right Column - Results */}
              <div className="space-y-6">
                {/* Main Results Card — flippable: front=Annual Savings, back=Hours Saved */}
                <div className="[perspective:1200px]">
                  <button
                    type="button"
                    onClick={() => setResultsFlipped((v) => !v)}
                    aria-label={
                      language === 'nl'
                        ? 'Wissel tussen jaarlijkse besparing en uren bespaard'
                        : 'Toggle between annual savings and hours saved'
                    }
                    data-testid="roi-result-flip-btn"
                    className={`relative w-full h-72 transition-transform duration-700 [transform-style:preserve-3d] text-left ${resultsFlipped ? '[transform:rotateY(180deg)]' : ''}`}
                  >
                    {/* FRONT — Annual Savings */}
                    <div className="absolute inset-0 [backface-visibility:hidden]">
                      <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg p-6 text-white h-full flex flex-col justify-between cursor-pointer hover:shadow-xl transition-shadow">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-medium text-green-100">
                            {language === 'nl' ? 'Geschatte Jaarlijkse Besparing' : 'Estimated Annual Savings'}
                          </h3>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={(e) => e.stopPropagation()}
                                className="p-2 -m-2 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
                              >
                                <Info className="h-5 w-5 text-green-200 hover:text-white transition-colors" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-sm p-4 bg-white shadow-xl border border-gray-200">
                              <p className="text-sm font-semibold text-gray-900 mb-2">
                                {language === 'nl' ? 'Waarop is dit gebaseerd?' : 'What is this based on?'}
                              </p>
                              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                                {language === 'nl'
                                  ? 'Deze schatting is gebaseerd op gemiddelde marktdata en benchmarks van vergelijkbare HR AI-implementaties in organisaties van verschillende groottes.'
                                  : 'This estimate is based on average market data and benchmarks from similar HR AI implementations in organizations of various sizes.'}
                              </p>
                              <ul className="text-xs text-gray-500 space-y-1">
                                <li>• {language === 'nl' ? 'Tijdsbesparing: branche-gemiddelden' : 'Time savings: industry averages'}</li>
                                <li>• {language === 'nl' ? 'Uurlonen: gemiddelde HR-tarieven' : 'Hourly rates: average HR rates'}</li>
                                <li>• {language === 'nl' ? 'Kosten: leveranciersindicaties' : 'Costs: vendor indications'}</li>
                              </ul>
                              <p className="text-xs text-blue-600 mt-3 pt-2 border-t border-gray-100 font-medium">
                                {language === 'nl'
                                  ? 'Voor een nauwkeurigere berekening op basis van uw specifieke situatie, boek een gratis sessie.'
                                  : 'For a more accurate calculation based on your specific situation, book a free session.'}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>

                        <div>
                          <p className="text-5xl font-bold leading-none" data-testid="roi-annual-savings-value">
                            €{results.savings.toLocaleString()}
                          </p>
                          <p className="text-xs text-green-100 mt-1 opacity-80">
                            {language === 'nl' ? 'per jaar' : 'per year'}
                          </p>
                        </div>

                        {/* Per-period breakdown */}
                        <div className="grid grid-cols-3 gap-2 bg-white/10 rounded-xl p-3">
                          <div className="text-center">
                            <p className="text-xs text-green-100 opacity-80">{language === 'nl' ? '/ maand' : '/ month'}</p>
                            <p className="text-base font-semibold">€{Math.round(results.savings / 12).toLocaleString()}</p>
                          </div>
                          <div className="text-center border-x border-white/20">
                            <p className="text-xs text-green-100 opacity-80">{language === 'nl' ? '/ week' : '/ week'}</p>
                            <p className="text-base font-semibold">€{Math.round(results.savings / 52).toLocaleString()}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-green-100 opacity-80">{language === 'nl' ? '/ jaar' : '/ year'}</p>
                            <p className="text-base font-semibold">
                              €{results.savings.toLocaleString()}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-green-100 -mb-1">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span className="text-xs">
                              {results.savedHours.toLocaleString()} {language === 'nl' ? 'uren bespaard' : 'hours saved'}
                            </span>
                          </div>
                          <span className="text-xs opacity-75 italic">
                            {language === 'nl' ? 'Klik om uren te focussen →' : 'Click to focus hours →'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* BACK — Hours Saved with breakdown */}
                    <div className="absolute inset-0 [transform:rotateY(180deg)] [backface-visibility:hidden]">
                      <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg p-6 text-white h-full flex flex-col justify-between cursor-pointer hover:shadow-xl transition-shadow">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-medium text-blue-100">
                            {language === 'nl' ? 'Geschatte Uren Bespaard' : 'Estimated Hours Saved'}
                          </h3>
                          <Clock className="h-5 w-5 text-blue-200" />
                        </div>

                        <div>
                          <p className="text-5xl font-bold leading-none" data-testid="roi-hours-saved-value">
                            {results.savedHours.toLocaleString()}
                            <span className="text-xl font-medium text-blue-100 ml-2">
                              {language === 'nl' ? 'uur / jaar' : 'hours / year'}
                            </span>
                          </p>
                          {/* FTE equivalence — based on 1840 productive hours per FTE (BE standard: 38u × 48.4w) */}
                          <p className="text-xs text-blue-100 mt-1 opacity-90">
                            ≈ <span className="font-semibold text-white" data-testid="roi-fte-value">
                              {(results.savedHours / 1840).toFixed(2)}
                            </span>{' '}
                            {language === 'nl'
                              ? 'FTE vrijgespeeld (op basis van 1840u/jaar)'
                              : 'FTE freed up (based on 1840h/year)'}
                          </p>
                        </div>

                        {/* 12-month ramp-up sparkline (replaces the per-period breakdown on the back) */}
                        <div className="bg-white/10 rounded-xl px-3 py-2" data-testid="roi-rampup-sparkline">
                          <div className="flex items-center justify-between text-xs text-blue-100 mb-1">
                            <span>{language === 'nl' ? 'Uren bespaard per maand (12 mnd)' : 'Hours saved per month (12 mo)'}</span>
                            <span className="font-semibold text-white">
                              {Math.round(results.savedHours / 12)} {language === 'nl' ? 'u/mnd op cruise' : 'h/mo at cruise'}
                            </span>
                          </div>
                          {(() => {
                            const factors = [0, 0, 0.25, 0.5, 0.75, 1, 1, 1, 1, 1, 1, 1];
                            const monthly = results.savedHours / 12;
                            const points = factors.map((f) => f * monthly);
                            const w = 280;
                            const h = 36;
                            const pad = 2;
                            const max = monthly || 1;
                            const stepX = (w - pad * 2) / (points.length - 1);
                            const coords = points.map((v, i) => {
                              const x = pad + i * stepX;
                              const y = h - pad - (v / max) * (h - pad * 2);
                              return [x, y];
                            });
                            const linePath = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
                            const areaPath = `${linePath} L ${coords[coords.length - 1][0].toFixed(1)} ${h - pad} L ${coords[0][0].toFixed(1)} ${h - pad} Z`;
                            return (
                              <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-9 block" aria-hidden="true">
                                <defs>
                                  <linearGradient id="rampupGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.45" />
                                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                                  </linearGradient>
                                </defs>
                                <path d={areaPath} fill="url(#rampupGrad)" />
                                <path d={linePath} fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                                {coords.map(([x, y], i) => (
                                  <circle
                                    key={`c-${x}`}
                                    cx={x}
                                    cy={y}
                                    r={i === coords.length - 1 ? 2.6 : 1.6}
                                    fill="#ffffff"
                                    opacity={i < 2 ? 0.45 : 1}
                                  />
                                ))}
                              </svg>
                            );
                          })()}
                          <div className="flex items-center justify-between text-[10px] text-blue-100 opacity-80 -mt-0.5">
                            <span>M1</span>
                            <span className="italic">
                              {language === 'nl' ? 'Setup → full benefit vanaf M6' : 'Setup → full benefit from M6'}
                            </span>
                            <span>M12</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-blue-100 -mb-1">
                          <div className="flex items-center gap-2">
                            <Euro className="h-4 w-4" />
                            <span className="text-xs">
                              €{results.savings.toLocaleString()} {language === 'nl' ? 'bespaard per jaar' : 'saved per year'}
                            </span>
                          </div>
                          <span className="text-xs opacity-75 italic">
                            {language === 'nl' ? '← Klik om € te focussen' : '← Click to focus €'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>

                {/* ROI Metrics */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-blue-500" />
                      ROI Metrics
                    </h3>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="p-2 -m-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                          <Info className="h-4 w-4 text-gray-400 hover:text-blue-500 transition-colors" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs p-3 bg-white shadow-lg border border-gray-200">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {language === 'nl' 
                            ? 'Implementatiekosten zijn geschat op basis van de complexiteit van deze use case. De werkelijke kosten kunnen variëren afhankelijk van uw specifieke situatie en gekozen leverancier.'
                            : 'Implementation costs are estimated based on the complexity of this use case. Actual costs may vary depending on your specific situation and chosen vendor.'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Implementatiekosten</span>
                      <span className="font-semibold text-gray-900">{getCostRange()}</span>
                    </div>
                    
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Terugverdientijd</span>
                        <span className="font-semibold text-green-600">{getPaybackPeriod()}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Gebaseerd op gemiddelde implementatiekosten ({getCostRange()}) gedeeld door jaarlijkse besparing (€{results.savings.toLocaleString()})
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-yellow-50 rounded-lg border border-blue-200">
                      <span className="text-gray-700 font-medium">ROI eerste jaar</span>
                      <span className={`text-2xl font-bold ${getROIPercentage() >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {getROIPercentage() >= 0 ? '+' : ''}{getROIPercentage()}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* 3-Year Projection */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Euro className="h-5 w-5 text-green-500" />
                    3-Jaar Projectie
                  </h3>
                  
                  <div className="space-y-3">
                    {[1, 2, 3].map((year) => {
                      const avgCost = useCase?.difficulty === 'low' ? 10000 : useCase?.difficulty === 'medium' ? 27500 : 55000;
                      const cumulative = results.savings * year - avgCost;
                      return (
                        <div key={year} className="flex items-center justify-between">
                          <span className="text-gray-600">Jaar {year}</span>
                          <span className={`font-semibold ${cumulative >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {cumulative >= 0 ? '+' : ''}€{cumulative.toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate(`/use-cases/${id}/workshop`)}
                    className="w-full bg-blue-500 hover:bg-blue-600 py-6 text-lg"
                  >
                    <Calendar className="h-5 w-5 mr-2" />
                    Workshop Boeken
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={downloadReport}
                    className="w-full border-blue-500 text-blue-600 hover:bg-blue-50 py-6"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    Download Rapport
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    </TooltipProvider>
  );
};

export default ROICalculatorPage;
